<script>
  /**
   * Regular dates as a weekly plan: one column per weekday that has any, each series once with
   * its time, category and title. A series that does not meet every week says how often it
   * does ("3. Montag im Monat") and when next; a weekly one says nothing, unless its next date
   * is more than a week away. Below 900px the weekdays become rows.
   *
   * Every word comes from the translations or the data, so the plan reads right on any site;
   * a heading or an introduction is the place's own (the calendar's section head, a block's title).
   */
  import * as env from '$app/env/public';
  import { page } from '$app/state';
  import t from '#lib/i18n.svelte.js';
  import { entry_url, generate_css_variables, render_micro_markdown } from '#lib/util.js';
  import { get_entry_category } from '#lib/entry_category.js';
  import { parse_day } from '#lib/calendar_periods.js';
  import { time_label } from '#lib/calendar_events.js';
  import '#lib/system-styles/calendar.css';

  let {
    /** @type {Array<{ series_id: string, next: object, next_key: string, days_until: number, weekday: number, rhythm: { kind: string, nth?: number | 'last' } }>} */
    regular = [],
    class: css_class = '',
  } = $props();

  const LOCALE = env.PUBLIC_TIME_LOCALE || 'de-DE';
  const format = (options) => new Intl.DateTimeFormat(LOCALE, { timeZone: 'UTC', ...options });

  const days = $derived.by(() => {
    const groups = new Map();
    for (const series of regular) {
      if (!groups.has(series.weekday)) {
        const day = parse_day(series.next_key);
        groups.set(series.weekday, {
          weekday: series.weekday,
          long: format({ weekday: 'long' }).format(day),
          short: format({ weekday: 'short' }).format(day).replace(/\.$/, ''),
          items: [],
        });
      }
      groups.get(series.weekday).items.push(series);
    }
    return [...groups.values()];
  });

  const category_of = (event) => get_entry_category(event.tag_keys, page.data.entry_tag_colors);

  function note_of(series) {
    const next = t('next_date', { date: format({ day: 'numeric', month: 'short' }).format(parse_day(series.next_key)) });
    switch (series.rhythm?.kind) {
      case 'weekly':
        return series.days_until > 7 ? next : '';
      case 'every_other_week':
        return `${t('rhythm_every_other_week')} · ${next}`;
      case 'monthly':
        return `${t('rhythm_monthly', { nth: t(`ordinal_${series.rhythm.nth}`), weekday: format({ weekday: 'long' }).format(parse_day(series.next_key)) })} · ${next}`;
      default:
        return next;
    }
  }
</script>

{#if days.length > 0}
  <div class='calendar regular-plan {css_class}' style:--days={days.length}>
    {#each days as day (day.weekday)}
      <section class='regular-day' aria-label={day.long}>
        <h3 class='period-head'>
          <span class='period-name text-heading3 day-long'>{day.long}</span>
          <span class='period-name text-control day-short' aria-hidden='true'>{day.short}</span>
        </h3>
        <ul class='regular-list'>
          {#each day.items as series (series.series_id)}
            {@const category = category_of(series.next)}
            {@const note = note_of(series)}
            {@const time = (time_label(series.next) || t('all_day')).split('–')}
            <li>
              <a class='regular-item item-link' href={entry_url(series.next)}>
                <span class='meta text-caption time'>{time[0]}{#if time[1]}<span class='time-end'>–{time[1]}</span>{/if}</span>
                <span class='title-line'>
                  <span class='mark-slot'>
                    {#if category}<i class='mark' style={generate_css_variables(category.color_id)} aria-hidden='true'></i>{/if}
                  </span>
                  <span class='item-title text-control'>{@html render_micro_markdown(series.next.title || t('untitled'), [], 'text-control')}</span>
                </span>
                {#if note}<span class='meta text-caption note'>{note}</span>{/if}
              </a>
            </li>
          {/each}
        </ul>
      </section>
    {/each}
  </div>
{:else}
  <div class='calendar'>
    <p class='empty-note text-body'>{t('no_regular_dates')}</p>
  </div>
{/if}

<style lang='postcss'>
  .regular-plan {
    display: grid;
    grid-template-columns: repeat(var(--days), minmax(0, 1fr));
    gap: var(--size-7) var(--grid-gutter);
  }
  .regular-day {
    display: flex;
    flex-direction: column;
    min-width: 0;
  }
  .day-short {
    display: none;
  }
  .regular-list {
    list-style: none;
    margin: 0;
    padding: var(--size-4) 0 0;
    display: flex;
    flex-direction: column;
    gap: var(--size-5);
  }
  .regular-item {
    display: grid;
    grid-template-columns: minmax(0, 1fr);
    gap: var(--size-1);
  }
  .time {
    white-space: nowrap;
  }
  .title-line {
    display: grid;
    grid-template-columns: var(--calendar-mark-size) minmax(0, 1fr);
    align-items: baseline;
    column-gap: var(--size-3);
    .item-title {
      max-width: none;
      hyphens: auto;
    }
  }
  .mark-slot {
    display: flex;
  }
  .note {
    padding-inline-start: calc(var(--calendar-mark-size) + var(--size-3));
  }

  /* Narrow: the weekdays become rows, a short day name on the left, the time before the title */
  @media (max-width: 900px) {
    .regular-plan {
      grid-template-columns: minmax(0, 1fr);
      row-gap: var(--size-6);
    }
    .regular-day {
      display: grid;
      grid-template-columns: 2.6rem minmax(0, 1fr);
      column-gap: var(--size-4);
      align-items: baseline;
    }
    .period-head {
      border: 0;
      padding: 0;
    }
    .day-long {
      display: none;
    }
    .day-short {
      display: inline;
    }
    /* the start time is enough in the narrow time column */
    .time-end {
      display: none;
    }
    .regular-list {
      padding: 0;
      gap: var(--size-4);
    }
    .regular-item {
      grid-template-columns: 3.4rem minmax(0, 1fr);
      column-gap: var(--size-3);
      align-items: baseline;
      .note {
        grid-column: 2;
      }
    }
  }
</style>
